import type { FetchWithAuth } from "@/contexts/ApiContext";
import type { Block } from "@/lib/api";

/**
 * Sync transport for the Flow editor's continuous background sync.
 * Talks to the conditional batch endpoint and the sync-telemetry
 * ingest. See architecture/2026-05-21-flow-editor-save-model.md.
 */

export type SyncResult =
  | { kind: "ok"; version: number; blocks: Block[] }
  | { kind: "conflict" }
  /** 403 (not yours to edit) or 404 (gone, or never shared with you): no retry will change it. */
  | { kind: "refused"; status: 403 | 404 }
  | { kind: "error"; message: string };

/**
 * Push the working copy to the API, carrying the document's
 * optimistic-concurrency version. A 409 means another device/tab
 * advanced the document — the caller pulls the latest and resolves.
 */
export async function syncDocument(
  fetchWithAuth: FetchWithAuth,
  documentId: string,
  body: { blocks: Block[]; expectedVersion: number },
): Promise<SyncResult> {
  let response: Response;
  try {
    response = await fetchWithAuth(`/api/documents/${documentId}/blocks/batch`, {
      method: "POST",
      body: JSON.stringify({ blocks: body.blocks, expectedVersion: body.expectedVersion }),
    });
  } catch (err) {
    return { kind: "error", message: err instanceof Error ? err.message : "Network error" };
  }

  if (response.status === 409) return { kind: "conflict" };
  if (response.status === 403 || response.status === 404) return { kind: "refused", status: response.status };
  if (!response.ok) {
    return { kind: "error", message: `Sync failed (HTTP ${response.status})` };
  }

  const data = (await response.json().catch(() => null)) as
    | { blocks?: Block[]; version?: number }
    | null;
  if (!data || typeof data.version !== "number") {
    return { kind: "error", message: "Malformed sync response" };
  }
  return { kind: "ok", version: data.version, blocks: data.blocks ?? [] };
}

export interface SyncTelemetryEvent {
  eventKind: "conflict" | "sync_error" | "retry_exhausted" | "offline_span";
  severity?: "info" | "warn" | "error";
  documentId?: string;
  attemptCount?: number;
  durationMs?: number;
  detail?: string;
}

/**
 * Best-effort sync telemetry — offline spans, retry exhaustion,
 * conflicts. Never surfaces a failure to the user; a dropped telemetry
 * batch must not disrupt editing.
 */
export async function reportSyncTelemetry(
  fetchWithAuth: FetchWithAuth,
  events: SyncTelemetryEvent[],
): Promise<void> {
  if (events.length === 0) return;
  try {
    await fetchWithAuth("/api/sync/telemetry", {
      method: "POST",
      body: JSON.stringify({ events }),
    });
  } catch {
    /* telemetry is best-effort — swallow */
  }
}
